import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ActualCheckInStats from '../components/ActualCheckInStats';

// 이벤트 타입 결정 함수 (컴포넌트 외부에 정의)
const getEventType = (theme) => {
  if (theme === 'Regular SLAM Meet') {
    return 'REGULAR_MEET'; // 멤버십 이벤트
  }
  return 'SPECIAL_EVENT'; // 단일 티켓 이벤트 (Outing, Networking Party, etc.)
};

// 날짜 포맷 함수 (영어, 줄바꿈 포함)
const formatEventDateTime = (dateTime) => {
  const date = new Date(dateTime);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  
  return (
    <div>
      <div>{year}. {month}. {day}.</div>
      <div>{ampm} {displayHours}:{minutes}:{seconds}</div>
    </div>
  );
};

// --- 이벤트 생성/수정 폼 컴포넌트 ---
const EventForm = ({ event, onSave, onCancel, eventStatistics }) => {
  const [formData, setFormData] = useState(event);
  const [imageFile, setImageFile] = useState(null);
  const [customTheme, setCustomTheme] = useState('');
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [imgSrc, setImgSrc] = useState('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [showEarlyBirdInfo, setShowEarlyBirdInfo] = useState(false);
  const imgRef = useRef(null);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Theme이 변경되면 이벤트 타입도 자동으로 설정
    if (name === 'theme') {
      const eventType = getEventType(value);
      setFormData(prev => ({ ...prev, eventType }));
    }
  };
  
  const handleDateChange = (e) => {
    setFormData(prev => ({ ...prev, eventDateTime: e.target.value }));
  };

  // 이미지 크롭 관련 함수들
  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(e.target.files[0]);
      setShowCropModal(true);
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        16 / 9,
        width,
        height
      ),
      width,
      height
    ));
  };

  const getCroppedImg = (image, crop, fileName) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            return;
          }
          blob.name = fileName;
          resolve(blob);
        },
        'image/jpeg',
        0.8
      );
    });
  };

  const handleCropComplete = async () => {
    if (!imgRef.current || !completedCrop) return;

    try {
      const croppedImageBlob = await getCroppedImg(
        imgRef.current,
        completedCrop,
        'cropped-image.jpg'
      );
      
      const croppedFile = new File([croppedImageBlob], 'cropped-image.jpg', {
        type: 'image/jpeg',
      });
      
      setImageFile(croppedFile);
      setShowCropModal(false);
      setImgSrc('');
      setCrop(undefined);
      setCompletedCrop(undefined);
    } catch (error) {
      alert('이미지 크롭 중 오류가 발생했습니다.');
    }
  };



  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Early Bird 필수 필드 검증 (체크박스가 선택된 경우에만)
    if ((formData.earlyBirdUseDate || formData.earlyBirdUseCapacity) && (!formData.earlyBirdPrice || formData.earlyBirdPrice === '')) {
      alert('Early Bird Price is required when Early Bird Activation Criteria is enabled.');
      return;
    }
    
    if (formData.earlyBirdUseDate && !formData.earlyBirdEndDate) {
      alert('Early Bird End Date is required when "Use Date Limit" is enabled.');
      return;
    }
    
    if (formData.earlyBirdUseCapacity && (!formData.earlyBirdCapacity || formData.earlyBirdCapacity < 1)) {
      alert('Early Bird Capacity Limit is required and must be at least 1 when "Use Capacity Limit" is enabled.');
      return;
    }
    
    // Early Bird End Date가 Registration Deadline보다 늦을 수 없는 검증
    if (formData.earlyBirdEndDate && formData.registrationDeadline) {
      const earlyBirdDate = new Date(formData.earlyBirdEndDate);
      const registrationDate = new Date(formData.registrationDeadline);
      if (earlyBirdDate > registrationDate) {
        alert('Early Bird End Date cannot be later than Registration Deadline.');
        return;
      }
    }
    
    // Early Bird Capacity가 Total Capacity보다 큰 경우 검증 (체크박스 선택 여부와 관계없이)
    if (formData.earlyBirdCapacity && formData.capacity && formData.earlyBirdCapacity > formData.capacity) {
      alert(`Early Bird Capacity Limit (${formData.earlyBirdCapacity}) must be less than or equal to Total Capacity (${formData.capacity}).`);
      return;
    }
    
    const data = new FormData();
    data.append('branch', formData.branch);
    data.append('title', formData.title);
    if (formData.theme) data.append('theme', formData.theme);
    if (formData.eventType) data.append('eventType', formData.eventType);
    data.append('eventDateTime', formData.eventDateTime);
    if (formData.endTime) data.append('endTime', formData.endTime);
    data.append('location', formData.location);
    if (formData.description) data.append('description', formData.description);
    data.append('capacity', String(formData.capacity ?? 0));
    data.append('price', String(formData.price ?? 0));
    
    // ✅ 새로운 필드들 추가 - null/빈값 처리 개선
    // Early Bird 가격은 값이 있으면 항상 전송 (체크박스 선택 여부와 관계없이)
    if (formData.earlyBirdPrice && formData.earlyBirdPrice !== '') {
      data.append('earlyBirdPrice', String(formData.earlyBirdPrice));
    }
    // Early Bird 관련 필드들은 값이 있으면 항상 전송 (체크박스 선택 여부와 관계없이)
    if (formData.earlyBirdEndDate) {
      data.append('earlyBirdEndDate', formData.earlyBirdEndDate);
    }
    if (formData.earlyBirdCapacity && formData.earlyBirdCapacity !== '') {
      data.append('earlyBirdCapacity', String(formData.earlyBirdCapacity));
    }
    // ✅ Early Bird 체크박스 상태를 명확하게 전송
    data.append('earlyBirdUseDate', String(formData.earlyBirdUseDate === true));
    data.append('earlyBirdUseCapacity', String(formData.earlyBirdUseCapacity === true));
    if (formData.registrationDeadline) {
      data.append('registrationDeadline', formData.registrationDeadline);
    }
    if (formData.capacityWarningThreshold && formData.capacityWarningThreshold !== '') {
      data.append('capacityWarningThreshold', String(formData.capacityWarningThreshold));
    }
    data.append('showCapacityWarning', String(Boolean(formData.showCapacityWarning)));
    if (formData.capacityWarningType) {
      data.append('capacityWarningType', formData.capacityWarningType);
    }
    
    // ✅ 계좌 정보 추가 - 빈값이 아닐 때만 전송
    if (formData.bankAccount) {
      data.append('bankAccount', formData.bankAccount);
    }
    if (formData.bankName) {
      data.append('bankName', formData.bankName);
    }
    if (formData.accountName) {
      data.append('accountName', formData.accountName);
    }
    
    if (imageFile) {
      data.append('image', imageFile);
    } else if (formData.id && formData.imageUrl) {
      // 편집 중 새 이미지가 없고 기존 이미지가 있을 때: 백엔드에 기존 이미지 유지 요청
      data.append('retainImage', 'true');
    }
    
    onSave({ id: formData.id, formData: data, isMultipart: true });
  };

  return (
    <div className="p-4 sm:p-8">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{formData.id ? 'Edit Event' : 'Create New Event'}</h1>
      <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 rounded-lg shadow-md space-y-6">
        {/* ✅ Basic Event Information */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-200 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="bg-blue-100 p-2 rounded-lg mr-3">
              <span className="text-2xl">📝</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-800">Basic Event Information</h3>
              <p className="text-sm text-blue-600 font-medium">Required fields for event creation</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className="mt-1 block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Branch</label>
                <select name="branch" value={formData.branch} onChange={handleChange} className="mt-1 block w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option>NCCU</option>
                  <option>NTU</option>
                  <option>TAIPEI</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Theme</label>
                <select 
                  name="theme" 
                  value={formData.theme === customTheme && customTheme ? 'Others' : formData.theme || ''} 
                  onChange={(e) => {
                    if (e.target.value === 'Others') {
                      setFormData(prev => ({ ...prev, theme: '' }));
                    } else {
                      const selectedTheme = e.target.value;
                      const eventType = getEventType(selectedTheme);
                      setFormData(prev => ({ ...prev, theme: selectedTheme, eventType }));
                      setCustomTheme('');
                    }
                  }}
                  className="mt-1 block w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
              <option value="">Select a theme</option>
              <option value="Regular SLAM Meet">🤝 Regular SLAM Meet</option>
              <option value="Outing">🌳 Outing</option>
              <option value="Networking Party">🎉 Networking Party</option>
              <option value="Cultural Exchange">🌍 Cultural Exchange</option>
              <option value="Bar Night">🍻 Bar Night</option>
              <option value="Sports Activity">⚽ Sports Activity</option>
              <option value="Workshop">🛠️ Workshop</option>
              <option value="Others">✏️ Others (Custom)</option>
            </select>
            {(formData.theme === '' || formData.theme === customTheme) && (
              <input
                type="text"
                placeholder="Enter custom theme..."
                value={customTheme}
                onChange={(e) => {
                  const customThemeValue = e.target.value;
                  setCustomTheme(customThemeValue);
                  const eventType = getEventType(customThemeValue);
                  setFormData(prev => ({ ...prev, theme: customThemeValue, eventType }));
                }}
                className="mt-2 block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
                {/* 이벤트 타입 표시 */}
                {formData.theme && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm font-medium text-gray-700">Event Type: </span>
                    <span className={`text-sm font-semibold ${
                      formData.eventType === 'REGULAR_MEET' 
                        ? 'text-blue-600' 
                        : 'text-orange-600'
                    }`}>
                      {formData.eventType === 'REGULAR_MEET' ? '🤝 Membership Event' : '🎫 Single Ticket Event'}
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      {formData.eventType === 'REGULAR_MEET' 
                        ? 'Members can join for free, non-members need to purchase membership'
                        : 'Anyone can purchase a single ticket for this event'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* ✅ Date, Time & Location */}
        <div className="bg-gradient-to-r from-sky-50 to-blue-50 p-6 rounded-xl border-2 border-sky-200 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="bg-sky-100 p-2 rounded-lg mr-3">
              <span className="text-2xl">📅</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-sky-800">Date, Time & Location</h3>
              <p className="text-sm text-sky-600 font-medium">Event schedule and venue details</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date & Time</label>
              <input type="datetime-local" name="eventDateTime" value={formData.eventDateTime} onChange={handleDateChange} className="mt-1 block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">End Time</label>
              <input type="time" name="endTime" value={formData.endTime || ''} onChange={handleChange} className="mt-1 block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <div className="space-y-2">
                <input 
                  type="text" 
                  name="location" 
                  value={formData.location} 
                  onChange={handleChange} 
                  className="mt-1 block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" 
                  placeholder="e.g., NTU Main Library, NCCU Business Building, Taipei 101"
                  required 
                />
                {formData.location && (
                  <div className="flex items-center space-x-2">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-sm bg-purple-100 text-purple-800 px-3 py-2 rounded-lg hover:bg-purple-200 transition-colors"
                    >
                      <span>📍</span>
                      <span>Open in Google Maps to Confirm</span>
                    </a>
                    <span className="text-xs text-gray-500">Click to verify location</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* ✅ Event Image & Description */}
        <div className="bg-gradient-to-r from-cyan-50 to-teal-50 p-6 rounded-xl border-2 border-cyan-200 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="bg-cyan-100 p-2 rounded-lg mr-3">
              <span className="text-2xl">🖼️</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-cyan-800">Event Image & Description</h3>
              <p className="text-sm text-cyan-600 font-medium">Visual content and event details</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Event Image</label>
              <input 
                type="file" 
                accept="image/*" 
                onChange={onSelectFile} 
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
              />
              {formData.imageUrl && (
                <p className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded-lg border">
                  <span className="font-medium">Current:</span> {formData.imageUrl}
                </p>
              )}
              {!formData.imageUrl && !imageFile && (
                <p className="text-sm text-gray-500 mt-2 p-2 bg-gray-50 rounded-lg border">
                  No file selected
                </p>
              )}
              {imageFile && (
                <p className="text-sm text-green-600 mt-2 p-2 bg-green-50 rounded-lg border">
                  <span className="font-medium">✅ Selected:</span> {imageFile.name} (cropped)
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea 
                name="description" 
                value={formData.description || ''} 
                onChange={handleChange} 
                rows="8" 
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter event description..."
              ></textarea>
            </div>
          </div>
        </div>
        {/* ✅ Capacity, Price & Registration */}
        <div className="bg-gradient-to-r from-teal-50 to-green-50 p-6 rounded-xl border-2 border-teal-200 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="bg-teal-100 p-2 rounded-lg mr-3">
              <span className="text-2xl">💰</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-teal-800">Capacity, Price & Registration</h3>
              <p className="text-sm text-teal-600 font-medium">Event capacity, pricing and registration settings</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Capacity (Total Spots)</label>
                <input type="number" name="capacity" value={formData.capacity} onChange={handleChange} className="mt-1 block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Regular Price (NTD)</label>
                <input type="number" name="price" value={formData.price} onChange={handleChange} className="mt-1 block w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              </div>
            </div>

            {/* ✅ Registration Deadline */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Registration Deadline</label>
              <input 
                type="datetime-local" 
                name="registrationDeadline" 
                value={formData.registrationDeadline || ''} 
                onChange={handleChange} 
                className={`mt-1 block w-full p-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                  formData.earlyBirdEndDate && formData.registrationDeadline && 
                  new Date(formData.earlyBirdEndDate) > new Date(formData.registrationDeadline)
                    ? 'border-red-500 bg-red-50' 
                    : 'border-gray-300'
                }`}
              />
              {formData.earlyBirdEndDate && formData.registrationDeadline && 
               new Date(formData.earlyBirdEndDate) > new Date(formData.registrationDeadline) && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Registration Deadline must be later than Early Bird End Date
                </p>
              )}
              <div className="mt-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-gray-600 font-medium">
                  💡 Last date and time when users can register for the event
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Early Bird Pricing Section */}
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h3 className="text-lg font-semibold mb-3 text-yellow-800">🐦 Early Bird Pricing <span className="text-sm font-normal text-red-600">(Optional)</span></h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Early Bird Price (NTD)
                {(formData.earlyBirdUseDate || formData.earlyBirdUseCapacity) && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <input 
                type="number" 
                name="earlyBirdPrice" 
                value={formData.earlyBirdPrice || ''} 
                onChange={handleChange} 
                className={`mt-1 block w-full p-2 border rounded-md ${
                  (formData.earlyBirdUseDate || formData.earlyBirdUseCapacity) && (!formData.earlyBirdPrice || formData.earlyBirdPrice === '')
                    ? 'border-red-500 bg-red-50' 
                    : formData.earlyBirdPrice && formData.price && parseInt(formData.earlyBirdPrice) >= parseInt(formData.price)
                    ? 'border-red-500 bg-red-50'
                    : ''
                }`}
                placeholder="e.g. 500"
                required={(formData.earlyBirdUseDate || formData.earlyBirdUseCapacity)}
              />
              {(formData.earlyBirdUseDate || formData.earlyBirdUseCapacity) && (!formData.earlyBirdPrice || formData.earlyBirdPrice === '') && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Early Bird Price is required when Early Bird Activation Criteria is enabled
                </p>
              )}
              {formData.earlyBirdPrice && formData.price && parseInt(formData.earlyBirdPrice) >= parseInt(formData.price) && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Early Bird Price must be less than Regular Price (NT${formData.price})
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Early Bird End Date
                {formData.earlyBirdUseDate && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <input 
                type="datetime-local" 
                name="earlyBirdEndDate" 
                value={formData.earlyBirdEndDate || ''} 
                onChange={handleChange} 
                disabled={!formData.earlyBirdUseDate}
                className={`mt-1 block w-full p-2 border rounded-md ${
                  formData.earlyBirdUseDate && !formData.earlyBirdEndDate 
                    ? 'border-red-500 bg-red-50' 
                    : !formData.earlyBirdUseDate
                    ? 'bg-gray-100 text-gray-500'
                    : ''
                }`}
                required={formData.earlyBirdUseDate}
              />
              {formData.earlyBirdUseDate && !formData.earlyBirdEndDate && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Early Bird End Date is required when "Use Date Limit" is enabled
                </p>
              )}
              {formData.earlyBirdEndDate && formData.registrationDeadline && 
               new Date(formData.earlyBirdEndDate) > new Date(formData.registrationDeadline) && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Early Bird End Date cannot be later than Registration Deadline
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Early Bird Capacity Limit
                {formData.earlyBirdUseCapacity && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <input 
                type="number" 
                name="earlyBirdCapacity" 
                value={formData.earlyBirdCapacity || ''} 
                onChange={handleChange} 
                disabled={!formData.earlyBirdUseCapacity}
                className={`mt-1 block w-full p-2 border rounded-md ${
                  (formData.earlyBirdUseCapacity && (!formData.earlyBirdCapacity || formData.earlyBirdCapacity < 1)) ||
                  (formData.earlyBirdCapacity && formData.capacity && formData.earlyBirdCapacity > formData.capacity)
                    ? 'border-red-500 bg-red-50' 
                    : !formData.earlyBirdUseCapacity
                    ? 'bg-gray-100 text-gray-500'
                    : ''
                }`}
                placeholder="e.g. 20"
                required={formData.earlyBirdUseCapacity}
                min={formData.earlyBirdUseCapacity ? "1" : undefined}
              />
              {formData.earlyBirdUseCapacity && (!formData.earlyBirdCapacity || formData.earlyBirdCapacity < 1) && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Early Bird Capacity Limit is required and must be at least 1 when "Use Capacity Limit" is enabled
                </p>
              )}
              {formData.earlyBirdCapacity && formData.capacity && formData.earlyBirdCapacity > formData.capacity && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ Early Bird Capacity Limit must be less than or equal to Total Capacity ({formData.capacity})
                </p>
              )}
            </div>
          </div>
          
          {/* ✅ Early Bird 활성화 기준 설정 */}
          <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">🎯 Early Bird Activation Criteria</h4>
            <div className="space-y-2">
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  name="earlyBirdUseDate" 
                  checked={formData.earlyBirdUseDate === true} 
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    earlyBirdUseDate: e.target.checked,
                    // 체크박스 해제 시 해당 필드 초기화
                    earlyBirdEndDate: e.target.checked ? prev.earlyBirdEndDate : ''
                  }))} 
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Use Date Limit (Early Bird End Date)</span>
              </label>
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  name="earlyBirdUseCapacity" 
                  checked={formData.earlyBirdUseCapacity === true} 
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    earlyBirdUseCapacity: e.target.checked,
                    // 체크박스 해제 시 해당 필드 초기화
                    earlyBirdCapacity: e.target.checked ? prev.earlyBirdCapacity : null
                  }))} 
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Use Capacity Limit (Early Bird Capacity Limit)</span>
              </label>
            </div>
          </div>
          
          {/* ✅ Early Bird 설명 (박스 바깥) */}
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-600">
              💡 Either criterion can be used (OR condition). At least one must be enabled for Early Bird to work.
            </p>
            <button
              type="button"
              onClick={() => setShowEarlyBirdInfo(!showEarlyBirdInfo)}
              className="ml-2 text-purple-500 hover:text-purple-700 text-sm"
              title="Click for detailed information"
            >
              ℹ️
            </button>
          </div>
          
          {/* ✅ Early Bird 상세 정보 (토글) */}
          {showEarlyBirdInfo && (
            <div className="mt-2 p-3 bg-white rounded border border-gray-200">
              <div className="space-y-2 text-xs text-gray-800">
                <p>
                  <strong>📋 Early bird conditions:</strong> When either the deadline or capacity limit is reached, pricing switches to regular rate.
                </p>
                <p>
                  <strong>💬 Message Display:</strong> If only "Use Date Limit" is selected, Early Bird will show "Early Bird Special is now active!" message. 
                  If "Use Capacity Limit" is selected, it will show "X spots left!" message.
                </p>
                <p>
                  <strong>🧮 Capacity Calculation:</strong> Early Bird Capacity Limit is calculated based on "Whole Applicants" 
                  (Approved + Pending members for Membership Events, Paid + Pending tickets for Single Ticket Events).
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ✅ Capacity Warning Settings */}
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h3 className="text-lg font-semibold mb-3 text-yellow-800">⚠️ Capacity Warning Settings <span className="text-sm font-normal text-red-600">(Optional)</span></h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Warning Threshold (Spots Left)</label>
            <input 
              type="number" 
              name="capacityWarningThreshold" 
              value={formData.capacityWarningThreshold || ''} 
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({ 
                  ...prev, 
                  capacityWarningThreshold: value ? parseInt(value) : null,
                  showCapacityWarning: value ? true : false
                }));
              }} 
              className="mt-1 block w-full p-2 border rounded-md" 
              placeholder="e.g. 20"
            />
            <p className="text-xs text-gray-600 mt-1">
              💡 When spots left ≤ this number, show "Hurry up! Only X spots left!" warning message
            </p>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Warning Based On</label>
            <select 
              name="capacityWarningType" 
              value={formData.capacityWarningType || 'ACTIVE_MEMBERS'} 
              onChange={(e) => setFormData(prev => ({ ...prev, capacityWarningType: e.target.value }))} 
              className="mt-1 block w-full p-2 border rounded-md"
            >
              <option value="ACTIVE_MEMBERS">Approved Membership Holders</option>
              <option value="PENDING_MEMBERS">Whole Applicants (Including pending + membership list)</option>
              <option value="RSVP_ATTENDEES">Actual event Attendees</option>
            </select>
            <p className="text-xs text-gray-600 mt-1">
              💡 This warning is for Regular price FOMO (independent from Early Bird Capacity)
            </p>
            
            {/* ✅ Capacity Warning Messages (Early Bird Style) */}
            {(() => {
              const stats = formData.id ? eventStatistics[formData.id] : null;
              const activeMembers = stats?.activeMembers || 0;
              const wholeApplicants = stats?.wholeApplicants || 0;
              const actualAttendees = stats?.actualAttendees || 0;
              const capacity = formData.capacity || 0;
              
              let currentCount = 0;
              let warningType = '';
              
              // 현재 선택된 타입에 따른 카운트 확인
              switch (formData.capacityWarningType) {
                case 'ACTIVE_MEMBERS':
                  currentCount = activeMembers;
                  warningType = 'Approved Membership Holders';
                  break;
                case 'PENDING_MEMBERS':
                  currentCount = wholeApplicants;
                  warningType = 'Whole Applicants';
                  break;
                case 'RSVP_ATTENDEES':
                  currentCount = actualAttendees;
                  warningType = 'Actual event Attendees';
                  break;
              }
              
              // Capacity를 초과하는 경우 빨간색 경고문 표시
              if (currentCount > capacity && formData.capacityWarningType) {
                return (
                  <p className="text-xs text-red-600 mt-1">
                    ⚠️ {warningType} ({currentCount}) exceeds event capacity ({capacity}). This will show negative spots left (-{currentCount - capacity} spots left). Please consider using a different warning type or increasing the event capacity.
                  </p>
                );
              }
              return null;
            })()}
          </div>

          {/* ✅ Real-time Capacity Status Display */}
          <div className="mt-4 p-3 bg-white rounded-lg border border-yellow-300">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">📊 Current Status by Warning Type:</h4>
            {(() => {
              const stats = formData.id ? eventStatistics[formData.id] : null;
              const activeMembers = stats?.activeMembers || 0;
              const wholeApplicants = stats?.wholeApplicants || 0;
              const actualAttendees = stats?.actualAttendees || 0;
              const capacity = formData.capacity || 0;
              
              // 새 이벤트 생성 시에는 통계 데이터가 없으므로 안내 메시지 표시
              if (!formData.id) {
                return (
                  <div className="text-center text-gray-500 text-sm py-4">
                    📊 Real-time statistics will be available after saving the event
                  </div>
                );
              }
              
              return getEventType(formData.theme) === 'REGULAR_MEET' ? (
                // Membership Event
                <div className="space-y-2 text-xs">
                  <div className={`flex justify-between items-center p-2 rounded ${formData.capacityWarningType === 'ACTIVE_MEMBERS' ? 'bg-blue-50 border border-blue-200' : ''}`}>
                    <span className={`font-medium ${formData.capacityWarningType === 'ACTIVE_MEMBERS' ? 'text-blue-800' : ''}`}>
                      Approved Membership Holders:
                      {formData.capacityWarningType === 'ACTIVE_MEMBERS' && <span className="text-blue-500 ml-1">*</span>}
                    </span>
                    <span className={`px-2 py-1 rounded ${capacity >= activeMembers ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {activeMembers} / {capacity} ({capacity ? (capacity - activeMembers) : 0} spots left)
                    </span>
                  </div>
                  <div className={`flex justify-between items-center p-2 rounded ${formData.capacityWarningType === 'PENDING_MEMBERS' ? 'bg-blue-50 border border-blue-200' : ''}`}>
                    <span className={`font-medium ${formData.capacityWarningType === 'PENDING_MEMBERS' ? 'text-blue-800' : ''}`}>
                      Whole Applicants:
                      {formData.capacityWarningType === 'PENDING_MEMBERS' && <span className="text-blue-500 ml-1">*</span>}
                    </span>
                    <span className={`px-2 py-1 rounded ${capacity >= wholeApplicants ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {wholeApplicants} / {capacity} ({capacity ? (capacity - wholeApplicants) : 0} spots left)
                    </span>
                  </div>
                  <div className={`flex justify-between items-center p-2 rounded ${formData.capacityWarningType === 'RSVP_ATTENDEES' ? 'bg-blue-50 border border-blue-200' : ''}`}>
                    <span className={`font-medium ${formData.capacityWarningType === 'RSVP_ATTENDEES' ? 'text-blue-800' : ''}`}>
                      Actual event Attendees:
                      {formData.capacityWarningType === 'RSVP_ATTENDEES' && <span className="text-blue-500 ml-1">*</span>}
                    </span>
                    <span className={`px-2 py-1 rounded ${capacity >= actualAttendees ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {actualAttendees} / {capacity} ({capacity ? (capacity - actualAttendees) : 0} spots left)
                    </span>
                  </div>
                </div>
              ) : (
                // Single Ticket Event
                <div className="space-y-2 text-xs">
                  <div className={`flex justify-between items-center p-2 rounded ${formData.capacityWarningType === 'ACTIVE_MEMBERS' ? 'bg-blue-50 border border-blue-200' : ''}`}>
                    <span className={`font-medium ${formData.capacityWarningType === 'ACTIVE_MEMBERS' ? 'text-blue-800' : ''}`}>
                      Paid Tickets:
                      {formData.capacityWarningType === 'ACTIVE_MEMBERS' && <span className="text-blue-500 ml-1">*</span>}
                    </span>
                    <span className={`px-2 py-1 rounded ${capacity >= (stats?.paidTickets || 0) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {stats?.paidTickets || 0} / {capacity} ({capacity ? (capacity - (stats?.paidTickets || 0)) : 0} spots left)
                    </span>
                  </div>
                  <div className={`flex justify-between items-center p-2 rounded ${formData.capacityWarningType === 'PENDING_MEMBERS' ? 'bg-blue-50 border border-blue-200' : ''}`}>
                    <span className={`font-medium ${formData.capacityWarningType === 'PENDING_MEMBERS' ? 'text-blue-800' : ''}`}>
                      Whole Applicants (Paid + Pending):
                      {formData.capacityWarningType === 'PENDING_MEMBERS' && <span className="text-blue-500 ml-1">*</span>}
                    </span>
                    <span className={`px-2 py-1 rounded ${capacity >= ((stats?.paidTickets || 0) + (stats?.pendingTickets || 0)) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {(stats?.paidTickets || 0) + (stats?.pendingTickets || 0)} / {capacity} ({capacity ? (capacity - ((stats?.paidTickets || 0) + (stats?.pendingTickets || 0))) : 0} spots left)
                    </span>
                  </div>
                  <div className={`flex justify-between items-center p-2 rounded ${formData.capacityWarningType === 'RSVP_ATTENDEES' ? 'bg-blue-50 border border-blue-200' : ''}`}>
                    <span className={`font-medium ${formData.capacityWarningType === 'RSVP_ATTENDEES' ? 'text-blue-800' : ''}`}>
                      Actual event Attendees:
                      {formData.capacityWarningType === 'RSVP_ATTENDEES' && <span className="text-blue-500 ml-1">*</span>}
                    </span>
                    <span className={`px-2 py-1 rounded ${capacity >= actualAttendees ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {actualAttendees} / {capacity} ({capacity ? (capacity - actualAttendees) : 0} spots left)
                    </span>
                  </div>
                </div>
              );
            })()}
            <p className="text-xs text-orange-600 mt-2">
              ⚠️ If any status shows negative spots, increase Capacity (Total Spots) above the highest number
            </p>
          </div>
        </div>

        {/* ✅ Bank Information Settings */}
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <h3 className="text-lg font-semibold mb-3 text-yellow-800">🏦 Bank Transfer Information <span className="text-sm font-normal text-red-600">(Optional)</span></h3>
          <p className="text-xs text-gray-600 mb-4">
            💡 If provided, this will override the default branch account info for membership payments
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Bank Name</label>
              <input 
                type="text" 
                name="bankName" 
                value={formData.bankName || ''} 
                onChange={handleChange} 
                className="mt-1 block w-full p-2 border rounded-md" 
                placeholder="e.g. (822) Cathay United Bank"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Number</label>
              <input 
                type="text" 
                name="bankAccount" 
                value={formData.bankAccount || ''} 
                onChange={handleChange} 
                className="mt-1 block w-full p-2 border rounded-md" 
                placeholder="e.g. 123-456-7890"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Name</label>
              <input 
                type="text" 
                name="accountName" 
                value={formData.accountName || ''} 
                onChange={handleChange} 
                className="mt-1 block w-full p-2 border rounded-md" 
                placeholder="e.g. SLAM NTU"
              />
            </div>
          </div>
        </div>





        <div className="flex flex-col sm:flex-row justify-end gap-4 pt-4">
          <button type="button" onClick={onCancel} className="bg-gray-200 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
          <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium shadow-sm">Save Event</button>
        </div>
      </form>

      {/* 이미지 크롭 모달 */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">Crop Image</h3>
              <p className="text-sm text-gray-600 mb-4">
                Drag to select the desired area. The image will be automatically adjusted to 16:9 aspect ratio.
              </p>
              
              <div className="mb-4">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={16 / 9}
                  minWidth={200}
                  minHeight={112}
                >
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={imgSrc}
                    style={{ maxHeight: '400px', maxWidth: '100%' }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCropModal(false);
                    setImgSrc('');
                    setCrop(undefined);
                    setCompletedCrop(undefined);
                  }}
                  className="bg-gray-200 px-4 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCropComplete}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Crop Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminEventsPage() {
  const { user } = useUser();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [isEditing, setIsEditing] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [showPastEventModal, setShowPastEventModal] = useState(false);
  const [pastEventDetails, setPastEventDetails] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [branchFilter, setBranchFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('ALL');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [eventStatistics, setEventStatistics] = useState({});

  // 권한 확인 - STAFF 이상 접근 가능
  if (!user?.isLoggedIn || !['STAFF', 'LEADER', 'PRESIDENT', 'ADMIN'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200); // iPad Pro까지 카드 뷰 사용
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 이벤트별 통계 가져오기
  const fetchEventStatistics = async (eventId) => {
    try {
      const response = await axios.get(`/api/admin/events/${eventId}/statistics/current`);
      return response.data;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/admin/events');
        
        // ✅ 백엔드에서 누락된 필드들에 기본값 설정
        const eventsWithDefaults = response.data.map(event => ({
          ...event,
          // 이벤트 타입 설정 (Theme 기반)
          eventType: event.eventType || getEventType(event.theme) || 'SPECIAL_EVENT',
          earlyBirdPrice: event.earlyBirdPrice || null,
          earlyBirdEndDate: event.earlyBirdEndDate || null,
          earlyBirdCapacity: event.earlyBirdCapacity || null,
          registrationDeadline: event.registrationDeadline || null,
          capacityWarningThreshold: event.capacityWarningThreshold || null,
          showCapacityWarning: event.showCapacityWarning || false,
          bankName: event.bankName || '',
          bankAccount: event.bankAccount || '',
          accountName: event.accountName || ''
        }));
        
        setEvents(eventsWithDefaults);

        // ✅ 각 이벤트의 통계 데이터 가져오기
        const statisticsPromises = eventsWithDefaults.map(event => 
          fetchEventStatistics(event.id).then(stats => ({ eventId: event.id, stats }))
        );
        const statisticsResults = await Promise.all(statisticsPromises);
        
        const statisticsMap = {};
        statisticsResults.forEach(({ eventId, stats }) => {
          if (stats) {
            statisticsMap[eventId] = stats;
          }
        });
        
        setEventStatistics(statisticsMap);
      } catch (error) {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const now = new Date();
  
  // 필터링 함수
  const filterEvents = (events) => {
    return events.filter(event => {
      // 지부 필터
      const branchMatch = branchFilter === 'ALL' || event.branch === branchFilter;
      
      // 날짜 필터
      let dateMatch = true;
      if (dateFilter !== 'ALL') {
        const eventDate = new Date(event.eventDateTime);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        switch (dateFilter) {
          case 'TODAY':
            dateMatch = eventDate.toDateString() === today.toDateString();
            break;
          case 'THIS_WEEK':
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            dateMatch = eventDate >= weekStart && eventDate <= weekEnd;
            break;
          case 'THIS_MONTH':
            dateMatch = eventDate.getMonth() === today.getMonth() && eventDate.getFullYear() === today.getFullYear();
            break;
          case 'NEXT_MONTH':
            const nextMonth = new Date(today);
            nextMonth.setMonth(today.getMonth() + 1);
            dateMatch = eventDate.getMonth() === nextMonth.getMonth() && eventDate.getFullYear() === nextMonth.getFullYear();
            break;
          case '6_MONTHS':
            const sixMonthsAgo = new Date(today);
            sixMonthsAgo.setMonth(today.getMonth() - 6);
            dateMatch = eventDate >= sixMonthsAgo;
            break;
          case '1_YEAR':
            const oneYearAgo = new Date(today);
            oneYearAgo.setFullYear(today.getFullYear() - 1);
            dateMatch = eventDate >= oneYearAgo;
            break;
          case 'CUSTOM':
            if (customDateRange.start && customDateRange.end) {
              const startDate = new Date(customDateRange.start);
              const endDate = new Date(customDateRange.end);
              endDate.setHours(23, 59, 59, 999); // Include the entire end date
              dateMatch = eventDate >= startDate && eventDate <= endDate;
            } else {
              dateMatch = true; // If no custom range set, show all
            }
            break;
        }
      }
      
      return branchMatch && dateMatch;
    });
  };
  
  // Upcoming Events: archived되지 않은 이벤트만 (수동으로만 숨김)
  const upcomingEvents = filterEvents(events.filter(e => !e.archived)).sort((a, b) => new Date(a.eventDateTime) - new Date(b.eventDateTime));
  
  // Past Membership Events: archived된 멤버십 이벤트만 (REGULAR_MEET 타입)
  const pastMembershipEvents = filterEvents(events.filter(e => 
    e.archived && e.eventType === 'REGULAR_MEET'
  )).sort((a, b) => new Date(b.eventDateTime) - new Date(a.eventDateTime));
  
  // Past Ticket Events: archived된 티켓 이벤트만 (SPECIAL_EVENT 타입)
  const pastTicketEvents = filterEvents(events.filter(e => 
    e.archived && e.eventType === 'SPECIAL_EVENT'
  )).sort((a, b) => new Date(b.eventDateTime) - new Date(a.eventDateTime));
  
  // 모든 Past Events (호환성을 위해 유지)
  const pastEvents = [...pastMembershipEvents, ...pastTicketEvents];

  const handleAddNew = () => {
    // ✅ 모든 필드를 빈 문자열 또는 0으로 명확하게 초기화합니다.
    const defaultDescription = `Join us for an amazing event! 🎉

📍 What to expect:
• Meet amazing people from around the world
• Enjoy great food and drinks
• Fun activities and games
• Networking opportunities

🎫 What's included:
• Welcome drink
• Light refreshments
• Fun activities

👥 Who should attend:
• International students
• Local students
• Young professionals
• Anyone looking to make new friends!

Can't wait to see you there! 🌟`;

    // 1st/2nd/3rd 로테이션으로 기본 제목 생성
    const cycleNumber = ((events.length % 3) + 1);
    const ordinals = ['1st', '2nd', '3rd'];
    const defaultTitle = `SLAM ${ordinals[cycleNumber - 1]} Event`;

    setCurrentEvent({ 
      id: null, branch: 'NCCU', title: defaultTitle, eventDateTime: '', endTime: '',
      location: '', description: defaultDescription, imageUrl: '', theme: '',
      capacity: 0, price: 0, eventType: 'SPECIAL_EVENT', // 기본값을 단일 티켓으로 설정
      // ✅ 새로운 필드들 기본값
      earlyBirdPrice: null, earlyBirdEndDate: '', earlyBirdCapacity: null,
      earlyBirdUseDate: false, earlyBirdUseCapacity: false,
      registrationDeadline: '', capacityWarningThreshold: null, showCapacityWarning: false,
      // ✅ 계좌 정보 기본값
      bankName: '', bankAccount: '', accountName: ''
    });
    setIsEditing(true);
  };

  const handleEdit = (event) => {
    const formattedEvent = { 
      ...event, 
      eventDateTime: event.eventDateTime.slice(0, 16),
      // ✅ 날짜 필드들 포맷팅
      earlyBirdEndDate: event.earlyBirdEndDate ? event.earlyBirdEndDate.slice(0, 16) : '',
      registrationDeadline: event.registrationDeadline ? event.registrationDeadline.slice(0, 16) : '',
      // ✅ 이벤트 타입 설정 (Theme 기반)
      eventType: event.eventType || getEventType(event.theme) || 'SPECIAL_EVENT',
      // ✅ Early Bird 필드들 보존
      earlyBirdPrice: event.earlyBirdPrice || null,
      earlyBirdCapacity: event.earlyBirdCapacity || null,
      earlyBirdUseDate: event.earlyBirdUseDate === true, // 명시적으로 true인 경우만
      earlyBirdUseCapacity: event.earlyBirdUseCapacity === true, // 명시적으로 true인 경우만
      // ✅ Capacity Warning 필드들 보존
      capacityWarningThreshold: event.capacityWarningThreshold || null,
      showCapacityWarning: event.showCapacityWarning || false,
      // ✅ Bank 정보 필드들 보존
      bankName: event.bankName || '',
      bankAccount: event.bankAccount || '',
      accountName: event.accountName || ''
    };
    
    setCurrentEvent(formattedEvent);
    setIsEditing(true);
  };

  // ✅ 이벤트 복제 함수 (내용만 복사, 새로운 이벤트 생성)
  const handleDuplicate = (event) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 7); // 1주일 후로 설정
    const futureDateTime = tomorrow.toISOString().slice(0, 16);

    const duplicatedEvent = {
      ...event,
      id: null, // 새로운 이벤트이므로 ID 제거
      title: `${event.title} (Copy)`, // 제목에 (Copy) 추가
      eventDateTime: futureDateTime, // 1주일 후로 설정
      // 이벤트 타입 설정 (Theme 기반)
      eventType: event.eventType || getEventType(event.theme) || 'SPECIAL_EVENT',
      // 날짜 관련 필드들 초기화
      earlyBirdEndDate: '',
      registrationDeadline: '',
      // 참석자 관련 필드들 초기화
      currentAttendees: 0,
      archived: false
    };

    setCurrentEvent(duplicatedEvent);
    setIsEditing(true);
  };
  
  const handleSave = async (payload) => {
    const isNew = !payload.id;
    const url = isNew ? '/api/admin/events' : `/api/admin/events?eventId=${payload.id}`;
    const method = isNew ? 'post' : 'put';

    try {
      let response;
      if (payload.isMultipart) {
        response = await axios[method](url, payload.formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await axios[method](url, payload);
      }
      
      alert(`Event ${isNew ? 'created' : 'updated'} successfully!`);
      if (isNew) {
        setEvents(prev => [...prev, response.data]);
      } else {
        setEvents(prev => prev.map(e => e.id === payload.id ? response.data : e));
      }
      setIsEditing(false);
      setCurrentEvent(null);
    } catch (error) {
      alert(`Failed to save event: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDelete = async (eventId) => {
    if (window.confirm("Are you sure you want to delete this event? This action cannot be undone.")) {
      try {
        await axios.delete(`/api/admin/events/${eventId}`);
        alert("Event deleted successfully.");
        setEvents(prev => prev.filter(e => e.id !== eventId));
      } catch (error) {
        alert(`Failed to delete event: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const handleArchive = async (eventId) => {
    if (!window.confirm("Mark this event as Past (archive)?")) return;
    try {
      // 이벤트를 과거 날짜로 변경하여 Past Events로 이동
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      // LocalDateTime 형식으로 변환 (YYYY-MM-DDTHH:mm:ss) - Z 제거
      const pastDateTime = yesterday.toISOString().slice(0, 19);
      
      // 현재 이벤트 정보 찾기
      const currentEvent = events.find(e => e.id === eventId);
      if (!currentEvent) {
        alert('Event not found');
        return;
      }
      
      // FormData를 사용하여 백엔드 요구사항에 맞춤
      const formData = new FormData();
      formData.append('eventId', eventId);
      formData.append('branch', currentEvent.branch); // ✅ branch 파라미터 추가
      formData.append('title', currentEvent.title);
      formData.append('theme', currentEvent.theme || '');
      formData.append('eventDateTime', pastDateTime);
      formData.append('endTime', currentEvent.endTime || '');
      formData.append('location', currentEvent.location);
      formData.append('description', currentEvent.description || '');
      formData.append('capacity', String(currentEvent.capacity || 0));
      formData.append('price', String(currentEvent.price || 0));
      formData.append('archived', 'true');
      
      await axios.put(`/api/admin/events`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('Event moved to Past Events successfully.');
      
      // 로컬 상태 업데이트
      setEvents(prev => prev.map(e => 
        e.id === eventId 
          ? { ...e, eventDateTime: pastDateTime, archived: true } 
          : e
      ));
    } catch (error) {
      alert(`Failed to archive event: ${error.response?.data?.message || error.message}`);
    }
  };

  // ✅ Past Events를 Upcoming으로 되돌리는 함수
  const handleRestoreToUpcoming = async (eventId) => {
    if (!window.confirm("이 이벤트를 Upcoming Events로 되돌리시겠습니까?")) return;
    try {
      // 미래 날짜로 변경하여 Upcoming Events로 이동
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      // LocalDateTime 형식으로 변환 (YYYY-MM-DDTHH:mm:ss) - Z 제거
      const futureDateTime = tomorrow.toISOString().slice(0, 19);
      
      // 현재 이벤트 정보 찾기
      const currentEvent = events.find(e => e.id === eventId);
      if (!currentEvent) {
        alert('Event not found');
        return;
      }
      
      // FormData를 사용하여 백엔드 요구사항에 맞춤
      const formData = new FormData();
      formData.append('eventId', eventId);
      formData.append('branch', currentEvent.branch); // ✅ branch 파라미터 추가
      formData.append('title', currentEvent.title);
      formData.append('theme', currentEvent.theme || '');
      formData.append('eventDateTime', futureDateTime);
      formData.append('endTime', currentEvent.endTime || '');
      formData.append('location', currentEvent.location);
      formData.append('description', currentEvent.description || '');
      formData.append('capacity', String(currentEvent.capacity || 0));
      formData.append('price', String(currentEvent.price || 0));
      formData.append('archived', 'false');
      
      await axios.put(`/api/admin/events`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('Event restored to Upcoming Events successfully.');
      
      // 로컬 상태 업데이트
      setEvents(prev => prev.map(e => 
        e.id === eventId 
          ? { ...e, eventDateTime: futureDateTime, archived: false } 
          : e
      ));
    } catch (error) {
      alert(`Failed to restore event: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleViewPastEventDetails = async (event) => {
    try {
      // 이벤트 기본 정보
      const eventDetails = {
        ...event,
        attendanceRate: event.capacity > 0 ? ((event.currentAttendees || 0) / event.capacity * 100).toFixed(1) : 0
      };

      // RSVP 정보 가져오기 (참석자 및 After Party)
      try {
        const rsvpResponse = await axios.get(`/api/events/${event.id}/rsvp-summary`);
        eventDetails.rsvpData = rsvpResponse.data;
      } catch (error) {
        eventDetails.rsvpData = {
          totalAttending: event.currentAttendees || 0,
          afterPartyCount: 0,
          attendees: []
        };
      }

      // 실제 체크인 통계 가져오기
      try {
        const actualStatsResponse = await axios.get(`/api/admin/events/actual-attendance-stats?eventId=${event.id}`);
        eventDetails.actualStats = actualStatsResponse.data;
      } catch (error) {
        eventDetails.actualStats = null;
      }

      // Feedback 정보 가져오기
      try {
        const feedbackResponse = await axios.get(`/api/feedback/event/${event.id}/summary`);
        eventDetails.feedbackData = feedbackResponse.data;
      } catch (error) {
        eventDetails.feedbackData = {
          count: 0,
          overallAvg: 0,
          npsAvg: 0
        };
      }

      // 이벤트 통계 정보 가져오기 (Dashboard에서 보던 데이터)
      try {
        const statisticsResponse = await axios.get(`/api/admin/events/${event.id}/statistics/pre-event`);
        if (statisticsResponse.data && !statisticsResponse.data.message) {
          eventDetails.statisticsData = statisticsResponse.data;
        } else {
          // Pre-event 스냅샷이 없으면 현재 통계 사용
          const currentStatsResponse = await axios.get(`/api/admin/events/${event.id}/statistics/current`);
          eventDetails.statisticsData = currentStatsResponse.data;
        }
      } catch (error) {
        eventDetails.statisticsData = {
          totalCapacity: event.capacity || 0,
          currentAttendees: event.currentAttendees || 0,
          localMembers: 0,
          internationalMembers: 0,
          localPercentage: 0,
          internationalPercentage: 0
        };
      }

      setPastEventDetails(eventDetails);
      setShowPastEventModal(true);
    } catch (error) {
      alert('Failed to load event details: ' + (error.response?.data?.message || error.message));
    }
  };

  
  if (isEditing) {
    return <EventForm event={currentEvent} onSave={handleSave} onCancel={() => {
      setIsEditing(false);
      setCurrentEvent(null);
      // 추가 상태 초기화로 확실하게 Event Management 페이지로 복귀
      setActiveTab('upcoming');
      setBranchFilter('ALL');
      setDateFilter('ALL');
    }} eventStatistics={eventStatistics} />;
  }

    return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Event Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Create, edit, and manage upcoming and past events for all branches</p>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Branch Filter */}
            <div>
              <label htmlFor="branch-filter" className="block text-sm font-medium text-gray-700 mb-1">Filter by Branch</label>
              <select 
                id="branch-filter"
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="ALL">All Branches</option>
                <option value="NCCU">NCCU</option>
                <option value="NTU">NTU</option>
                <option value="TAIPEI">TAIPEI</option>
              </select>
            </div>
            
            {/* Date Filter */}
            <div>
              <label htmlFor="date-filter" className="block text-sm font-medium text-gray-700 mb-1">Filter by Date</label>
              <select 
                id="date-filter"
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  if (e.target.value === 'CUSTOM') {
                    setShowCustomDatePicker(true);
                  } else {
                    setShowCustomDatePicker(false);
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="ALL">All Dates</option>
                <option value="TODAY">Today</option>
                <option value="THIS_WEEK">This Week</option>
                <option value="THIS_MONTH">This Month</option>
                <option value="6_MONTHS">6 Months</option>
                <option value="1_YEAR">1 Year</option>
                <option value="CUSTOM">Custom</option>
              </select>
              
              {/* Custom Date Range Picker */}
              {showCustomDatePicker && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={customDateRange.start}
                        onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                      <input
                        type="date"
                        value={customDateRange.end}
                        onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => {
                        setDateFilter('ALL');
                        setShowCustomDatePicker(false);
                        setCustomDateRange({ start: '', end: '' });
                      }}
                      className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => setShowCustomDatePicker(false)}
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200 font-medium"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {(user?.role === 'LEADER' || user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
            <button onClick={handleAddNew} className="bg-blue-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-blue-700 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm">
              + Create New Event
            </button>
          )}
        </div>

        <div className="border-b border-gray-200">
          <nav className={`-mb-px flex gap-2 sm:gap-6 ${isMobile ? 'flex-col sm:flex-row' : 'overflow-x-auto'}`}>
            <button onClick={() => setActiveTab('upcoming')} className={`py-2 sm:py-4 px-1 border-b-2 font-medium text-sm sm:text-base ${isMobile ? 'w-full text-center' : 'whitespace-nowrap'} ${activeTab === 'upcoming' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>Upcoming Events</button>
            <button onClick={() => setActiveTab('past')} className={`py-2 sm:py-4 px-1 border-b-2 font-medium text-sm sm:text-base ${isMobile ? 'w-full text-center' : 'whitespace-nowrap'} ${activeTab === 'past' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>Past Membership Events ({pastMembershipEvents.length})</button>
            <button onClick={() => setActiveTab('past_tickets')} className={`py-2 sm:py-4 px-1 border-b-2 font-medium text-sm sm:text-base ${isMobile ? 'w-full text-center' : 'whitespace-nowrap'} ${activeTab === 'past_tickets' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>Past Ticket Events ({pastTicketEvents.length})</button>
          </nav>
        </div>

        <div className="mt-8">
        {loading ? <p>Loading...</p> : (
          activeTab === 'upcoming' ? (
            isMobile ? (
              // Mobile Card View for Upcoming Events
              <div className="space-y-4">
                {upcomingEvents.map(event => (
                  <div key={event.id} className="bg-white p-4 rounded-lg shadow-md border">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{event.title}</h3>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {event.branch}
                          </span>
                          {event.theme && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                              {event.theme}
                            </span>
                          )}
                          {event.eventType && (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              event.eventType === 'REGULAR_MEET' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {event.eventType === 'REGULAR_MEET' ? '🤝 Membership' : '🎫 Single Ticket'}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">{formatEventDateTime(event.eventDateTime)}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => handleEdit(event)} className="text-blue-600 hover:text-blue-900 text-sm font-medium">Edit</button>
                      <button onClick={() => handleDuplicate(event)} className="hidden md:inline text-green-600 hover:text-green-900 text-sm font-medium">📋 Duplicate</button>
                      <button onClick={() => window.open(`/events/${event.id}/qr-code`, '_blank')} className="text-purple-600 hover:text-purple-900 text-sm font-medium">📱 QR Code</button>
                      <button onClick={() => handleDelete(event.id)} className="text-red-600 hover:text-red-900 text-sm font-medium">Delete</button>
                      <button onClick={() => handleArchive(event.id)} className="hidden md:inline text-gray-700 hover:text-black text-sm font-medium">Move to Past</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop Table View for Upcoming Events
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/5">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/12">Branch</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/8">Theme</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/6">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/3">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {upcomingEvents.map(event => (
                    <tr key={event.id}>
                      <td className="px-4 py-3 text-sm font-semibold">{event.title}</td>
                      <td className="px-4 py-3 text-sm">{event.branch}</td>
                      <td className="px-4 py-3 text-sm">
                        {event.theme ? (
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                              {event.theme}
                            </span>
                            {event.eventType && (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                event.eventType === 'REGULAR_MEET' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {event.eventType === 'REGULAR_MEET' ? '🤝 Membership' : '🎫 Single Ticket'}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">No theme</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatEventDateTime(event.eventDateTime)}</td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {(user?.role === 'LEADER' || user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => handleEdit(event)} className="text-blue-600 hover:text-blue-900 text-sm">Edit</button>
                            <button onClick={() => handleDuplicate(event)} className="hidden md:inline text-green-600 hover:text-green-900 text-sm">📋 Duplicate</button>
                            <button onClick={() => window.open(`/events/${event.id}/qr-code`, '_blank')} className="text-purple-600 hover:text-purple-900 text-sm">📱 QR Code</button>
                            <button onClick={() => handleDelete(event.id)} className="text-red-600 hover:text-red-900 text-sm">Delete</button>
                            <button onClick={() => handleArchive(event.id)} className="hidden md:inline text-gray-700 hover:text-black text-sm">Move to Past</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )
          ) : activeTab === 'past' ? (
            isMobile ? (
              // Mobile Card View for Past Membership Events
              <div className="space-y-4">
                {pastMembershipEvents.map(event => (
                  <div key={event.id} className="bg-white p-4 rounded-lg shadow-md border">
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-900">{event.title}</h3>
                      {event.theme && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          <div className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full inline-block">
                            {event.theme}
                          </div>
                          {event.eventType && (
                            <div className={`text-xs px-2 py-0.5 rounded-full inline-block ${
                              event.eventType === 'REGULAR_MEET' 
                                ? 'text-blue-600 bg-blue-100' 
                                : 'text-orange-600 bg-orange-100'
                            }`}>
                              {event.eventType === 'REGULAR_MEET' ? '🤝 Membership' : '🎫 Single Ticket'}
                            </div>
                          )}
                        </div>
                      )}
                      <p className="text-sm text-gray-600 mt-1">{event.branch}</p>
                      <p className="text-sm text-gray-500">{new Date(event.eventDateTime).toLocaleDateString()}</p>
                      <div className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">{event.currentAttendees || 0} / {event.capacity || 0}</span>
                        <span className="text-xs text-green-600 ml-2">
                          {event.capacity > 0 ? `${((event.currentAttendees || 0) / event.capacity * 100).toFixed(1)}%` : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button 
                        onClick={() => handleViewPastEventDetails(event)}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                      >
                        View Details
                      </button>
                      {(user?.role === 'LEADER' || user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
                        <>
                          <button 
                            onClick={() => handleEdit(event)} 
                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDuplicate(event)} 
                            className="hidden md:inline text-green-600 hover:text-green-900 text-sm font-medium"
                          >
                            📋 Duplicate
                          </button>
                          <button 
                            onClick={() => window.open(`/events/${event.id}/qr-code`, '_blank')} 
                            className="text-purple-600 hover:text-purple-900 text-sm font-medium"
                          >
                            📱 QR Code
                          </button>
                          <button 
                            onClick={() => handleRestoreToUpcoming(event.id)} 
                            className="text-orange-600 hover:text-orange-900 text-sm font-medium"
                          >
                            ↗️ Restore to Upcoming
                          </button>
                        </>
                      )}
                      {(user?.role === 'ADMIN' || user?.role === 'PRESIDENT') && (
                        <button 
                          onClick={() => handleDelete(event.id)} 
                          className="text-red-600 hover:text-red-900 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop Table View for Past Events
              <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/5">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/12">Branch</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/6">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/6">Attendance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
              {pastMembershipEvents.map(event => (
                      <tr key={event.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{event.title}</div>
                              {event.theme && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  <div className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full inline-block">
                                    {event.theme}
                                  </div>
                                  {event.eventType && (
                                    <div className={`text-xs px-2 py-0.5 rounded-full inline-block ${
                                      event.eventType === 'REGULAR_MEET' 
                                        ? 'text-blue-600 bg-blue-100' 
                                        : 'text-orange-600 bg-orange-100'
                                    }`}>
                                      {event.eventType === 'REGULAR_MEET' ? '🤝 Membership' : '🎫 Single Ticket'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {event.branch}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(event.eventDateTime).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex flex-col">
                            <span className="font-medium">{event.currentAttendees || 0} / {event.capacity || 0}</span>
                            <span className="text-xs text-green-600">
                              {event.capacity > 0 ? `${((event.currentAttendees || 0) / event.capacity * 100).toFixed(1)}%` : 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          <button 
                            onClick={() => handleViewPastEventDetails(event)}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            View Details
                          </button>
                          {(user?.role === 'LEADER' || user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
                            <>
                              <button 
                                onClick={() => handleEdit(event)} 
                                className="text-indigo-600 hover:text-indigo-900 mr-4"
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => handleDuplicate(event)} 
                                className="text-green-600 hover:text-green-900 mr-4"
                              >
                                📋 Duplicate
                              </button>
                              <button 
                                onClick={() => handleRestoreToUpcoming(event.id)} 
                                className="text-orange-600 hover:text-orange-900 mr-4"
                              >
                                ↗️ Restore
                              </button>
                            </>
                          )}
                          {(user?.role === 'ADMIN' || user?.role === 'PRESIDENT') && (
                            <button 
                              onClick={() => handleDelete(event.id)} 
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : activeTab === 'past_tickets' ? (
            isMobile ? (
              // Mobile Card View for Past Ticket Events
              <div className="space-y-4">
                {pastTicketEvents.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No past ticket events found.</p>
                  </div>
                ) : (
                  pastTicketEvents.map(event => (
                    <div key={event.id} className="bg-white p-4 rounded-lg shadow-md border">
                      <div className="mb-3">
                        <h3 className="font-semibold text-gray-900">{event.title}</h3>
                        {event.theme && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                              {event.theme}
                            </span>
                          </div>
                        )}
                        <p className="text-sm text-gray-600 mt-1">{event.branch}</p>
                        <p className="text-sm text-gray-500">{new Date(event.eventDateTime).toLocaleDateString()}</p>
                        <p className="text-sm text-gray-500">Price: {event.price} NTD</p>
                        <p className="text-sm text-gray-500">Attendees: {event.currentAttendees || 0} / {event.capacity || 0}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button 
                          onClick={() => handleViewPastEventDetails(event)}
                          className="text-blue-600 hover:text-blue-900 text-sm"
                        >
                          View Details
                        </button>
                        {(user?.role === 'LEADER' || user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
                          <>
                            <button 
                              onClick={() => handleEdit(event)} 
                              className="text-indigo-600 hover:text-indigo-900 text-sm"
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDuplicate(event)} 
                              className="text-green-600 hover:text-green-900 text-sm"
                            >
                              📋 Duplicate
                            </button>
                            <button 
                              onClick={() => handleRestoreToUpcoming(event.id)} 
                              className="text-orange-600 hover:text-orange-900 text-sm"
                            >
                              ↗️ Restore
                            </button>
                          </>
                        )}
                        {(user?.role === 'ADMIN' || user?.role === 'PRESIDENT') && (
                          <button 
                            onClick={() => handleDelete(event.id)} 
                            className="text-red-600 hover:text-red-900 text-sm"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              // Desktop Table View for Past Ticket Events
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/5">Event Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/12">Branch</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/6">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/12">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/12">Attendees</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pastTicketEvents.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                          No past ticket events found.
                        </td>
                      </tr>
                    ) : (
                      pastTicketEvents.map(event => (
                        <tr key={event.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{event.title}</div>
                                {event.theme && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                      {event.theme}
                                    </span>
                                    {event.eventType && (
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        event.eventType === 'REGULAR_MEET' 
                                          ? 'bg-blue-100 text-blue-800' 
                                          : 'bg-orange-100 text-orange-800'
                                      }`}>
                                        {event.eventType === 'REGULAR_MEET' ? '🤝 Membership' : '🎫 Single Ticket'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {event.branch}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {new Date(event.eventDateTime).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {event.price} NTD
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            <div className="flex flex-col">
                              <span className="font-medium">{event.currentAttendees || 0} / {event.capacity || 0}</span>
                              <span className="text-xs text-green-600">
                                {event.capacity > 0 ? `${((event.currentAttendees || 0) / event.capacity * 100).toFixed(1)}%` : 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            <button 
                              onClick={() => handleViewPastEventDetails(event)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              View Details
                            </button>
                            {(user?.role === 'LEADER' || user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
                              <>
                                <button 
                                  onClick={() => handleEdit(event)} 
                                  className="text-indigo-600 hover:text-indigo-900 mr-4"
                                >
                                  Edit
                                </button>
                                <button 
                                  onClick={() => handleDuplicate(event)} 
                                  className="text-green-600 hover:text-green-900 mr-4"
                                >
                                  📋 Duplicate
                                </button>
                                <button 
                                  onClick={() => handleRestoreToUpcoming(event.id)} 
                                  className="text-orange-600 hover:text-orange-900 mr-4"
                                >
                                  ↗️ Restore
                                </button>
                              </>
                            )}
                            {(user?.role === 'ADMIN' || user?.role === 'PRESIDENT') && (
                              <button 
                                onClick={() => handleDelete(event.id)} 
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )
          ) : null
        )}
      </div>

      {/* Past Event Details Modal */}
      {showPastEventModal && pastEventDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4" onClick={() => setShowPastEventModal(false)}>
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{pastEventDetails.title}</h2>
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-600">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">{pastEventDetails.branch}</span>
                    {pastEventDetails.theme && (
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium">{pastEventDetails.theme}</span>
                    )}
                    <span className="text-gray-500">{new Date(pastEventDetails.eventDateTime).toLocaleDateString()}</span>
                  </div>
                </div>
                <button onClick={() => setShowPastEventModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
              {/* Feedback Section */}
              <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                <h3 className="text-lg font-semibold flex items-center mb-4">
                  <span className="mr-2">💬</span>
                  Feedback
                </h3>
                <div className="flex gap-4">
                  <button 
                    onClick={() => {
                      setShowPastEventModal(false);
                      window.location.href = `/admin/feedback?eventId=${pastEventDetails.id}&autoOpen=true`;
                    }}
                    className="flex-1 bg-indigo-100 text-indigo-700 px-4 py-3 rounded-lg hover:bg-indigo-200 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <span className="mr-2">👤</span>
                    Member
                  </button>
                  <button 
                    onClick={() => {
                      setShowPastEventModal(false);
                      window.location.href = `/admin/feedback?eventId=${pastEventDetails.id}&autoOpen=true&tab=staff`;
                    }}
                    className="flex-1 bg-purple-100 text-purple-700 px-4 py-3 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <span className="mr-2">👥</span>
                    Staff
                  </button>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                <h3 className="text-lg font-semibold flex items-center mb-4">
                  <span className="mr-2">📊</span>
                  Key Metrics
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-800">{pastEventDetails.feedbackData?.count || 0}</div>
                    <div className="text-sm text-gray-600">Total Submissions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-yellow-500">
                      {pastEventDetails.feedbackData?.overallAvg ? Number(pastEventDetails.feedbackData.overallAvg).toFixed(1) : 'N/A'} ★
                    </div>
                    <div className="text-sm text-gray-600">Overall Rating</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-blue-600">
                      {pastEventDetails.statisticsData?.currentAttendees > 0 
                        ? ((pastEventDetails.feedbackData?.count || 0) / pastEventDetails.statisticsData.currentAttendees * 100).toFixed(1)
                        : '0.0'
                      }%
                    </div>
                    <div className="text-sm text-gray-600">Response Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-green-500">
                      {pastEventDetails.feedbackData?.participantsFitAvg ? Number(pastEventDetails.feedbackData.participantsFitAvg).toFixed(1) : 'N/A'} ★
                    </div>
                    <div className="text-sm text-gray-600">Participants Fit</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-purple-500">
                      {pastEventDetails.feedbackData?.interactionOpportunityAvg ? Number(pastEventDetails.feedbackData.interactionOpportunityAvg).toFixed(1) : 'N/A'} ★
                    </div>
                    <div className="text-sm text-gray-600">Interaction Opportunity</div>
                  </div>
                </div>
              </div>

              {/* Registration Status */}
              {pastEventDetails.statisticsData && (
                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <span className="mr-2">📋</span>
                    Registration Status
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">{pastEventDetails.statisticsData.totalCapacity || 0}</div>
                      <div className="text-sm text-gray-600">Total Capacity</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-600">{pastEventDetails.statisticsData.currentAttendees || 0}</div>
                      <div className="text-sm text-gray-600">Attended</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-yellow-600">
                        {pastEventDetails.statisticsData.totalCapacity > 0 
                          ? ((pastEventDetails.statisticsData.currentAttendees || 0) / pastEventDetails.statisticsData.totalCapacity * 100).toFixed(1)
                          : '0.0'
                        }%
                      </div>
                      <div className="text-sm text-gray-600">Show-up Rate</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-600">{pastEventDetails.statisticsData.notAttendingCount || 0}</div>
                      <div className="text-sm text-gray-600">Not-Attended</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-orange-600">{pastEventDetails.statisticsData.noRsvpCount || 0}</div>
                      <div className="text-sm text-gray-600">No Response</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Actual Check-in Statistics */}
              <ActualCheckInStats actualStats={pastEventDetails.actualStats} />

              {/* After Party */}
              {pastEventDetails.statisticsData && (
                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <span className="mr-2">🎉</span>
                    After Party
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-xl font-bold text-purple-600">{pastEventDetails.statisticsData.afterPartyCount || 0}</div>
                      <div className="text-sm text-gray-600">After Party</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-indigo-600">
                        {(pastEventDetails.statisticsData.currentAttendees || 0) > 0 
                          ? ((pastEventDetails.statisticsData.afterPartyCount || 0) / (pastEventDetails.statisticsData.currentAttendees || 1) * 100).toFixed(1)
                          : '0.0'
                        }%
                      </div>
                      <div className="text-sm text-gray-600">After Party Rate</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Member Demographics */}
              {pastEventDetails.statisticsData && (
                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <span className="mr-2">👥</span>
                    Member Demographics
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{pastEventDetails.statisticsData.localMembers || 0}</div>
                      <div className="text-sm text-gray-600">🇹🇼 Local Members</div>
                      <div className="text-xs text-gray-500">{pastEventDetails.statisticsData.localPercentage ? pastEventDetails.statisticsData.localPercentage.toFixed(1) : 0}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{pastEventDetails.statisticsData.internationalMembers || 0}</div>
                      <div className="text-sm text-gray-600">🌍 International Members</div>
                      <div className="text-xs text-gray-500">{pastEventDetails.statisticsData.internationalPercentage ? pastEventDetails.statisticsData.internationalPercentage.toFixed(1) : 0}%</div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <div className="text-lg font-semibold text-gray-800">Total: {pastEventDetails.statisticsData.totalMembers || 0} Members</div>
                  </div>
                </div>
              )}

              {/* Event Details */}
              <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <span className="mr-2">📝</span>
                  Event Details
                </h3>
                <div className="space-y-3 text-sm">
                  <div><strong>Location:</strong> {pastEventDetails.location}</div>
                  <div><strong>Date & Time:</strong> {formatEventDateTime(pastEventDetails.eventDateTime)}</div>
                  {pastEventDetails.endTime && (
                    <div><strong>End Time:</strong> {pastEventDetails.endTime}</div>
                  )}
                  <div><strong>Price:</strong> {pastEventDetails.price ? `NT$${pastEventDetails.price}` : 'Free'}</div>
                  {pastEventDetails.description && (
                    <div>
                      <strong>Description:</strong>
                      <p className="mt-1 text-gray-600 whitespace-pre-wrap">{pastEventDetails.description}</p>
            </div>
        )}
      </div>
              </div>


              {/* Registered Attendees */}
              {pastEventDetails.rsvpData?.attendees && pastEventDetails.rsvpData.attendees.length > 0 && (
                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <span className="mr-2">👥</span>
                    Registered Attendees ({pastEventDetails.rsvpData.attendees.length})
                  </h3>
                  <div className="max-h-48 overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {pastEventDetails.rsvpData.attendees.map((attendee, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                          <span className="text-sm">{attendee.name || attendee.email}</span>
                          {attendee.afterParty && (
                            <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                              After Party
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-center pt-4 border-t">
                <button 
                  onClick={() => setShowPastEventModal(false)}
                  className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
              </div>
            </div>
                </div>
      )}

      </div>
    </div>
  );
}